import { useState } from 'react';
import { Pencil } from 'lucide-react';
import Button from '../ui/Button';
import userService from '../../services/user';
import { cn } from '../../utils/cn';

interface AdminEmailEditorProps {
  userId: number;
  email: string;
  onChanged: (email: string) => void;
  className?: string;
}

/**
 * Someone else's email address, with an inline "correct it" form for HR seat
 * holders and super admins (PATCH /users/:id/email). The server decides who
 * may change whose address and says why when it refuses, so the refusal is
 * shown verbatim rather than guessed at here.
 */
export default function AdminEmailEditor({ userId, email, onChanged, className }: AdminEmailEditorProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const close = () => {
    setEditing(false);
    setValue(email);
    setError(null);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await userService.adminChangeEmail(userId, value.trim());
      onChanged(value.trim());
      setNotice(res.message || 'Email changed.');
      setEditing(false);
    } catch (err: any) {
      setError(err?.message || 'Could not change the email address');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <div className={cn('min-w-0', className)}>
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm text-neutral-500 break-all">{email}</span>
          <button
            type="button"
            onClick={() => { setEditing(true); setNotice(null); setValue(email); }}
            className="flex-shrink-0 p-1 rounded text-neutral-400 hover:text-primary-600 hover:bg-neutral-100"
            title="Change email address"
            aria-label="Change email address"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        </div>
        {notice && <p className="text-xs text-green-700 mt-1">{notice}</p>}
      </div>
    );
  }

  return (
    <div className={cn('space-y-2 w-full', className)}>
      <label className="block text-xs text-neutral-500">
        New email address
        <input
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </label>
      <p className="text-xs text-neutral-500">
        They sign in with the new address straight away, and we email it a link to verify it.
      </p>
      {error && (
        <div className="rounded-md bg-error-50 border border-error-200 p-2 text-xs text-error-800">{error}</div>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} isLoading={busy} disabled={!value.trim() || value.trim() === email}>
          Save email
        </Button>
        <Button size="sm" variant="outline" onClick={close} disabled={busy}>Cancel</Button>
      </div>
    </div>
  );
}
